import { FC, useState } from "react";
import { motion } from "framer-motion";
import { frames, round, sum } from "@jurijtokarski/calc";
import { getDaysInMS, getWeeksInMS } from "@jurijtokarski/times";

import "./HistoryItems.css";

import { HistoryItem, Measure, MeasureLabels, ML_IN_OZ } from "../utils/context";
import { useStorage } from "../utils/useStorage";
import { findIconByAmount } from "../configs/amounts";
import { SimpleFadeIn } from "./SimpleFadeIn";
import { CloseIcon } from "./Icons";

interface HistoryItemsProps {
  items: HistoryItem[];
  onRemove: (item: HistoryItem) => void;
}

const Months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
] as const;

const buildDayIndex = (day: Date) => `${day.getFullYear()}${day.getMonth()}${day.getDate()}`;

const getStartDayTimestamp = (timestamp: number) => {
  const day = new Date(timestamp);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

const getEndDayTimestamp = (timestamp: number) => {
  const day = new Date(timestamp);
  day.setHours(23, 59, 59, 999);
  return day.getTime();
}

export const HistoryItems: FC<HistoryItemsProps> = ({ items, onRemove }) => {
  const { configMeasure, configGoal } = useStorage();

  const [isEdit, setEdit] = useState(false);
  const [removalQueue, setRemovalQueue] = useState<{ [key: number]: NodeJS.Timeout | null }>({});

  const today = new Date();
  const todayIndex = buildDayIndex(today);

  const from = items.length ? getStartDayTimestamp(items[items.length - 1].timestamp) : Date.now();
  const to = items.length ? getEndDayTimestamp(items[0].timestamp) : Date.now();

  const days = frames(getStartDayTimestamp(from) - getWeeksInMS(1), getEndDayTimestamp(to), getDaysInMS(1), true).reverse();
  const daysItems = days.map(([start, end]) => items.filter(i => i.timestamp >= start && i.timestamp < end));

  const yesterday = new Date(today.setDate(today.getDate() - 1));
  const yesterdayIndex = buildDayIndex(yesterday);

  const handleDelete = (item: HistoryItem) => setRemovalQueue({
    ...removalQueue,
    [item.timestamp]: setTimeout(() => onRemove(item), 3000)
  });

  const handleRestore = (item: HistoryItem) => {
    const timeoutId = removalQueue[item.timestamp];

    if (!timeoutId) {
      return;
    }

    clearTimeout(timeoutId);

    setRemovalQueue({
      ...removalQueue,
      [item.timestamp]: null
    });
  }

  const getTitleByDay = (timestamp: number) => {
    const day = new Date(timestamp);
    const dayIndex = buildDayIndex(day);

    if (dayIndex === todayIndex) {
      return "Today"
    }

    if (dayIndex === yesterdayIndex) {
      return "Yesterday";
    }

    return `${Months[day.getMonth()]} ${day.getDate()}`;
  }

  return (
    <SimpleFadeIn className="history-items">
      {days.map(([timestamp], index) => {
        const items = daysItems[index];
        const dayIndex = buildDayIndex(new Date(timestamp));

        const amount = sum(items.map(item => {
          if (item.measure === configMeasure) {
            return item.amount
          }

          if (item.measure === Measure.ML) {
            return item.amount / ML_IN_OZ;
          }

          return item.amount * ML_IN_OZ;
        }));

        const progress = amount / configGoal;
        const percentProgress = progress * 100;

        const dayAmounts = items.length > 0 && dayIndex === todayIndex ? (
          <div className={`history-items__day-items ${isEdit ? "history-items__day-items--edit-mode" : ""}`}>
            {items.map(item => {
              const amountIcon = findIconByAmount(item.amount, item.measure);
              const isRemoved = removalQueue[item.timestamp];

              const deleteButton = (
                <button
                  className={`history-items__day-item-button history-items__day-item-button--delete ${isEdit && !isRemoved ? "history-items__day-item-button--visible" : ""}`}
                  onClick={() => handleDelete(item)}
                >
                  <CloseIcon />
                </button>
              );

              const restoreButton = (
                <motion.button
                  className={`history-items__day-item-button history-items__day-item-button--restore ${isEdit && isRemoved ? "history-items__day-item-button--visible" : ""}`}
                  onClick={() => handleRestore(item)}
                >
                  ↺
                </motion.button>
              );

              const icon = amountIcon && (
                <svg viewBox="0 0 64 64" className="history-items__day-item-icon">
                  <path d={amountIcon} />
                </svg>
              );

              const amount = (
                <span className="history-items__day-item-amount">{item.amount} {MeasureLabels[item.measure]}</span>
              );

              return (
                <span className={`history-items__day-item ${isRemoved ? "history-items__day-item--removed" : ""}`}>
                  {deleteButton}
                  {restoreButton}
                  {icon}
                  {amount}
                </span>
              )
            })}
          </div>
        ) : null;

        const editItemsButton = items.length > 0 && dayIndex === todayIndex && (
          <button
            className={`history-items__day-summary-button ${isEdit ? "history-items__day-summary-button--active" : ""}`}
            onClick={() => setEdit(!isEdit)}
          >
            {isEdit ? "Done" : "Edit items"}
          </button>
        );

        return (
          <div className="history-items__day" key={dayIndex}>
            <h2 className="history-items__day-title">
              {getTitleByDay(timestamp)}
            </h2>
            <div className="history-items__day-summary">
              <div className="history-items__day-summary-progress">
                {round(progress * 100, 0)}% &middot; <strong>{amount} {MeasureLabels[configMeasure]}</strong>
              </div>
              {editItemsButton}
            </div>
            <div className="history-items__day-progress-bar">
              <div
                className={`history-items__day-progress ${progress >= 1 ? "history-items__day-progress--full" : ""}`}
                style={{
                  width: `${percentProgress > 100 ? 100 : percentProgress}%`
                }}
              />
            </div>
            {dayAmounts}
          </div>
        )
      })}
    </SimpleFadeIn >
  )
}